import { useState } from "react";

import { Loader2, Sparkles } from "lucide-react";

import { PhotoUpload } from "@/components/fields/photo-upload";
import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface TriggerParams {
  instagramPageUrl: string;
  instagramScreenshot?: string;
  place?: any;
}

interface VenueEnrichmentTriggerProps {
  venueName: string;
  isSubmitting: boolean;
  searchPlace: (query: string) => Promise<any[]>;
  onSubmit: (params: TriggerParams) => Promise<void>;
}

export function VenueEnrichmentTrigger({
  venueName,
  isSubmitting,
  searchPlace,
  onSubmit,
}: VenueEnrichmentTriggerProps) {
  const [selectedPlace, setSelectedPlace] = useState<any>(undefined);
  const [instagramPageUrl, setInstagramPageUrl] = useState("");
  const [instagramScreenshot, setInstagramScreenshot] = useState<string>("");

  const handleSubmit = () => {
    onSubmit({
      instagramPageUrl,
      instagramScreenshot: instagramScreenshot || undefined,
      place: selectedPlace,
    });
  };

  if (isSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-5 text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div>
          <p className="text-base font-medium">AI enrichment is running…</p>
          <p className="text-sm text-muted-foreground mt-1">
            This may take a minute or two. Please don't leave this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Venue Information</CardTitle>
          <CardDescription>
            Current venue to be enriched with AI-powered insights
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium">{venueName}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Google Maps</CardTitle>
          <CardDescription>
            Link to a Google Maps place to pull reviews, photos, and location
            data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RemoteSelect
            value={selectedPlace}
            onValueChanged={setSelectedPlace}
            onSearch={searchPlace}
            label="Search Google Places"
            triggerType="keyboard"
            accessorFn={(place: any) => {
              if (!place) return undefined;
              return {
                id: place.id,
                name: `${place.displayName?.text} @ ${place.shortFormattedAddress}`,
                number: place.rating ? `${place.rating} ⭐️` : undefined,
              };
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Instagram</CardTitle>
          <CardDescription>
            Provide an Instagram page URL and optionally a screenshot to pull
            bio and contact data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="instagramPageUrl">Instagram Page URL</Label>
            <Input
              id="instagramPageUrl"
              placeholder="https://www.instagram.com/venuename"
              value={instagramPageUrl}
              onChange={(e) => setInstagramPageUrl(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Instagram Screenshot (optional)</Label>
            <PhotoUpload
              value={instagramScreenshot}
              onChange={(value) => setInstagramScreenshot(value ?? "")}
              transformToWebP
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center pt-2">
        <Button size="lg" disabled={isSubmitting} onClick={handleSubmit}>
          <Sparkles className="h-4 w-4 mr-2" />
          Run AI Enrichment
        </Button>
      </div>
    </div>
  );
}
